import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, LayersControl } from 'react-leaflet';
import { Box, Typography, Paper, IconButton, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix pour les icônes Leaflet
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

export default function MapView({ address, name, postalCode, city, onClose }) {
  const [position, setPosition] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Construire l'adresse complète
  const fullAddressDisplay = `${address}, ${postalCode} ${city}`;

  useEffect(() => {
    const geocodeAddress = async () => {
      try {
        // Utiliser l'API adresse.data.gouv.fr pour La Réunion
        const fullAddress = `${address} ${postalCode} ${city} La Réunion`;
        
        // Essayer d'abord l'API française officielle
        let response = await fetch(
          `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(fullAddress)}&limit=1`
        );
        let data = await response.json();
        
        if (data.features && data.features.length > 0) {
          const coords = data.features[0].geometry.coordinates;
          setPosition([coords[1], coords[0]]); // [latitude, longitude]
          console.log('Position trouvée via API-Adresse:', coords[1], coords[0]);
        } else {
          // Si pas trouvé, essayer avec juste le code postal et la ville
          response = await fetch(
            `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(`${postalCode} ${city}`)}&limit=1`
          );
          data = await response.json();
          
          if (data.features && data.features.length > 0) {
            const coords = data.features[0].geometry.coordinates;
            setPosition([coords[1], coords[0]]);
            console.log('Position approximative trouvée:', coords[1], coords[0]);
          } else {
            // Positions par défaut pour les principales villes de La Réunion
            const defaultPositions = {
              'sainte-marie': [-20.8969, 55.5496],
              'saint-denis': [-20.8823, 55.4504],
              'saint-pierre': [-21.3393, 55.4781],
              'saint-paul': [-21.0097, 55.2695],
              'saint-louis': [-21.2861, 55.4099],
              'le tampon': [-21.2789, 55.5151],
              'saint-andre': [-20.9639, 55.6502],
              'saint-benoit': [-21.0359, 55.7132],
              'le port': [-20.9373, 55.2952],
              'la possession': [-20.9329, 55.3373],
              'saint-joseph': [-21.3776, 55.6163],
              'petite-ile': [-21.3553, 55.5667],
              'saint-leu': [-21.1710, 55.2886],
              'sainte-suzanne': [-20.9061, 55.6087],
              'saint-gilles-les-bains': [-21.0538, 55.2286]
            };
            
            const cityLower = city?.toLowerCase().replace(/[éèê]/g, 'e').replace(/[àâ]/g, 'a').replace(/[^a-z-\s]/g, '');
            let found = false;
            
            for (const [key, coords] of Object.entries(defaultPositions)) {
              if (cityLower?.includes(key) || key.includes(cityLower)) {
                setPosition(coords);
                console.log('Position par défaut pour', city, ':', coords);
                found = true;
                break;
              }
            }
            
            if (!found) {
              setPosition([-20.8823, 55.4504]); // Saint-Denis par défaut
            }
          }
        }
      } catch (error) {
        console.error('Erreur géocodage:', error);
        setPosition([-20.8823, 55.4504]);
      } finally {
        setLoading(false);
      }
    };
    
    geocodeAddress();
  }, [address, postalCode, city]);
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }
  
  const { BaseLayer } = LayersControl;
  
  return (
    <Paper
      sx={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '80%',
        maxWidth: '800px',
        height: '600px',
        zIndex: 1300,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: 2,
        borderBottom: '1px solid #e0e0e0'
      }}>
        <Box>
          <Typography variant="h6">{name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {fullAddressDisplay}
          </Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      
      <Box sx={{ flexGrow: 1 }}>
        <MapContainer
          center={position}
          zoom={16}
          style={{ height: '100%', width: '100%' }}
        >
          <LayersControl position="topright">
            {/* OpenStreetMap standard - Par défaut */}
            <BaseLayer checked name="OpenStreetMap">
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                maxZoom={19}
              />
            </BaseLayer>
            
            {/* ESRI World Imagery - Photos satellites */}
            <BaseLayer name="Satellite">
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                attribution='&copy; Esri, Maxar, Earthstar Geographics'
                maxZoom={19}
              />
            </BaseLayer>
            
            {/* Carte topographique - Relief */}
            <BaseLayer name="Topographique">
              <TileLayer
                url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
                attribution='Map data: &copy; OpenStreetMap, &copy; OpenTopoMap'
                maxZoom={17}
              />
            </BaseLayer>
            
            {/* CartoDB - Carte épurée */}
            <BaseLayer name="Carte claire">
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                attribution='&copy; OpenStreetMap &copy; CartoDB'
                maxZoom={19}
              />
            </BaseLayer>
            
            {/* CartoDB Dark - Mode sombre */}
            <BaseLayer name="Carte sombre">
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                attribution='&copy; OpenStreetMap &copy; CartoDB'
                maxZoom={19}
              />
            </BaseLayer>
          </LayersControl>
          
          <Marker position={position}>
            <Popup>
              <strong>{name}</strong><br />
              {fullAddressDisplay}
            </Popup>
          </Marker>
        </MapContainer>
      </Box>
    </Paper>
  );
}
